<template>
    <div class="apprentice-simple-card combined-personal-goals">
        <div class="component-title">
            <b-dropdown
                class="ellipsis-nav bordered-menu combined-personal-goal-nav"
                no-caret
                variant="primary"
                right
                toggle-class="combined-personal-goal-context-menu">
                <template v-slot:button-content>
                    <i class="fas fa-ellipsis-v"></i>
                </template>

                <b-dropdown-item
                    class="btn-new-personal-goal-set"
                    v-if="teacherAccess"
                    @click.prevent.stop="$refs.addCombinedPersonalGoal.show()">
                    <i class="btn-add"></i>
                    {{ $t('common.add_learning_goal') }}
                </b-dropdown-item>

                <b-dropdown-item
                    class="btn-new-gradable-moment"
                    v-if="teacherAccess && (personalGoals.length > 0 || skillTreesIn.length > 0)"
                    @click.prevent.stop="$refs.addGradableMoment.show()">
                    <i class="btn-add"></i>
                    {{ $t('personal_goals.evaluation_moment') }}
                </b-dropdown-item>

                <b-dropdown-item
                    class="btn-new-evaluate-moment"
                    v-if="teacherAccess && (personalGoals.length > 0 || skillTreesIn.length > 0)"
                    @click.prevent.stop="$refs.evaluateStudent.show()">
                    <i class="btn-add"></i>
                    {{ $t('personal_goals.student_discussion') }}
                </b-dropdown-item>

                <b-dropdown-item
                    class="btn-evaluation-overview"
                    v-if="(personalGoals.length > 0 || skillTreesIn.length > 0) && teacherAccess"
                    @click.prevent.stop="$refs.evaluateStudentOverview.show()">
                    <i class="fas fa-file-user fa-fw"></i>
                    {{ $t('personal_goals.student_discussion_overview') }}
                </b-dropdown-item>

                <b-dropdown-item
                    class="btn-show-personal-goals-history"
                    @click.prevent.stop="$refs.personalGoalsHistories.show()">
                    <i class="btn-history"></i>
                    {{ $t('personal_goals.history') }}
                </b-dropdown-item>
            </b-dropdown>

            <design-2021-combined-add-personal-goal-modal-component
                :ungraded-current-personal-goals-in="ungradedCurrentPersonalGoals"
                ref="addCombinedPersonalGoal"
                @store-personal-goal="storePersonalGoal">
            </design-2021-combined-add-personal-goal-modal-component>

            <design-2021-combined-add-gradable-moment-modal-component
                ref="addGradableMoment"
                :personal-goals-in="personalGoalsActive"
                :vso-skill-tree-in="vsoSkillTree"
                @store-grades="storeGrades">
            </design-2021-combined-add-gradable-moment-modal-component>

            <design-2021-combined-evaluate-student-modal-component
                ref="evaluateStudent"
                :personal-goals-in="personalGoals"
                @store-student-evaluation="storeStudentEvaluation"
                :student-evaluations-in="studentEvaluations">
            </design-2021-combined-evaluate-student-modal-component>

            <design-2021-combined-student-evaluation-overview-modal-component
                ref="evaluateStudentOverview"
                :personal-goals-in="personalGoals"
                :student-evaluations-in="studentEvaluations"
                :seasons-in="seasonsIn">
            </design-2021-combined-student-evaluation-overview-modal-component>

            <design-2021-combined-history-personal-goals-modal-component
                :personal-goals-in="personalGoals"
                ref="personalGoalsHistories">
            </design-2021-combined-history-personal-goals-modal-component>
        </div>

        <div class="header">
            <b-form-group
                :label="$t('common.school_year')"
                label-for="seasons-select">
                <b-select
                    class="apprentice-primary-select season-select"
                    id="interview-moments-select"
                    v-model="selectedSeason"
                    :options="seasons"
                    :disabled="seasons.length < 3">
                </b-select>
            </b-form-group>

            <b-form-group
                :label="$t('personal_goals.evaluation_moments')"
                label-for="interview-moments-select">
                <b-select
                    class="apprentice-primary-select interview-time-select"
                    id="interview-moments-select"
                    v-model="selectedGradableMomentId"
                    :options="gradableMomentsBySeason"
                    :disabled="gradableMomentsComputed.length < 2">
                </b-select>
            </b-form-group>
        </div>

        <div class="content">
            <div class="personal-goals-wrapper">
                <h4 class="title">{{ $t('common.learning_goals') }}</h4>
                <div
                    class="personal-goals"
                    v-if="selectedGradableMoment && selectedGradableMoment.personal_goal_grades.length > 0">
                    <design-2021-combined-personal-goal-card-component
                        v-for="personalGoalGrade of selectedGradableMoment.personal_goal_grades"
                        :key="personalGoalGrade.id"
                        :personal-goal-grade-in="personalGoalGrade"
                        @show-stats="showStatistics">
                    </design-2021-combined-personal-goal-card-component>
                </div>

                <div
                    class="personal-goals empty"
                    v-else>
                    <div class="not-found with-header">
                        <div class="icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </div>
                        <div class="alert-notice">{{ $t('personal_goals.no_goal_found') }}.</div>
                    </div>
                </div>
            </div>

            <div class="skills-wrapper">
                <h4 class="title">{{ $t('personal_goals.skills') }}</h4>
                <div
                    class="skills"
                    v-if="vsoSkillTree.skill_skill_trees.length > 0 && selectedGradableMoment">
                    <design-2021-combined-skill-card-component
                        v-for="skillSkillTree of vsoSkillTree.skill_skill_trees"
                        :key="skillSkillTree.id"
                        :skill-skill-tree-in="skillSkillTree"
                        :rubric-form-in="correctRubricForm(skillSkillTree.id)"
                        @show-stats="showStatistics">
                    </design-2021-combined-skill-card-component>
                </div>

                <div
                    class="skills empty"
                    v-else>
                    <div class="not-found with-header">
                        <div class="icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </div>
                        <div class="alert-notice">{{ $t('personal_goals.no_skills_found') }}</div>
                    </div>
                </div>
            </div>

            <design-2021-combined-personal-goal-statistics-modal-component ref="personalGoalStatistics">
            </design-2021-combined-personal-goal-statistics-modal-component>
        </div>
    </div>
</template>

<script>
export default {
    name: 'design-2021-combined-personal-goals-skills-component',
    props: {
        personalGoalsIn: {
            type: Array,
            required: true,
        },
        skillTreesIn: {
            type: Array,
            required: true,
        },
        seasonsIn: {
            type: Array,
            required: true,
        },
        userCredentialsIn: {
            type: Object,
            required: true,
        },
        teacherAccess: {
            type: Boolean,
            default: () => {
                return false;
            },
        },
        vsoSkillTreeIn: {
            type: Object,
            required: false,
        },
        gradableMomentsIn: {
            type: Array,
            required: false,
            default: [],
        },
        studentEvaluationsIn: {
            type: Array,
            required: false,
            default: [],
        },
    },

    data() {
        return {
            personalGoals: this.personalGoalsIn,
            gradableMoments: this.gradableMomentsIn,
            selectedSeason:
                this.personalGoalsIn.length > 0
                    ? this.seasonsIn.filter((season) => season.isCurrentSeason === true)[0].id
                    : null,
            selectedGradableMomentId:
                this.gradableMomentsIn.length === 0
                    ? null
                    : this.gradableMomentsIn[this.gradableMomentsIn.length - 1].id,
            vsoSkillTree: this.vsoSkillTreeIn,
            studentEvaluations: this.studentEvaluationsIn,
            ungradedCurrentPersonalGoals: this.personalGoalsIn.length
                ? !this.personalGoalsIn[this.personalGoalsIn.length - 1].grades.length
                : false,
            gradableMomentsUpdater: 0,
            personalGoalsUpdater: 0,
        };
    },

    computed: {
        gradableMomentsBySeason() {
            this.gradableMomentsUpdater;
            const gradableMoments = this.gradableMomentsComputed
                .filter((gradableMoment) => gradableMoment.season_id === this.selectedSeason)
                .map((gradableMoment) => {
                    gradableMoment.value = gradableMoment.id;
                    if (gradableMoment.id !== 0) {
                        gradableMoment.text = new Date(gradableMoment.created_at)
                            .toLocaleDateString('nl-NL')
                            .replace(/\//g, '-');
                    }
                    return gradableMoment;
                });

            gradableMoments.unshift({
                value: null,
                text: this.$t('personal_goals.choose_evaluation_moment'),
                disabled: true,
            });

            return gradableMoments;
        },

        personalGoalsActive() {
            this.personalGoalsUpdater;
            return this.personalGoals
                .filter((goal) => goal.archived === 0)
                .map((goal) => {
                    goal.grade = null;
                    return goal;
                });
        },

        gradableMomentsComputed() {
            this.gradableMomentsUpdater;
            this.personalGoalsUpdater;
            let gradableMoments = this.gradableMoments.filter((gradableMoment) => gradableMoment.id !== 0);
            let columns = null;
            gradableMoments.forEach((gradableMoment) => {
                gradableMoment = gradableMoment.rubric_forms.map((rubric_form) => {
                    this.vsoSkillTreeIn.skill_skill_trees.forEach((skillSkillTree) => {
                        const rubric = skillSkillTree.rubrics.find((rubric) => rubric.id === rubric_form.rubric_id);
                        if (rubric) {
                            columns = rubric.rubric_sections[0].rubric_columns;
                        }
                    });

                    rubric_form.rubric_columns = columns;
                    return rubric_form;
                });
            });

            let activeGoals = this.personalGoals.filter((goal) => goal.archived === 0);
            let personal_goal_grades = [];
            activeGoals.forEach((activeGoal, index) => {
                if (!activeGoal.grades.length) {
                    personal_goal_grades.push({
                        created_at: new Date(),
                        updated_at: new Date(),
                        gradable_moment_id: 0,
                        grade: '',
                        id: index === 0 ? 'a' : 'b',
                        personal_goal: activeGoal,
                        personal_goal_id: activeGoal.id,
                    });
                }
            });

            if (activeGoals[0] && !activeGoals[0].grades.length) {
                this.ungradedCurrentPersonalGoals = true;
                let rubricForms = this.vsoSkillTreeIn.skill_skill_trees.map((skillSkillTree) => {
                    return {
                        created_at: new Date(),
                        id: skillSkillTree.id + 1,
                        rubric_form_results: [
                            {
                                created_at: new Date(),
                                feedback: null,
                                id: skillSkillTree.id + 1,
                                max_points: 1,
                                result: 0,
                                rubric_form_id: skillSkillTree.id + 1,
                                updated_at: new Date(),
                                user_id: 0,
                            },
                        ],
                        rubric_columns: null,
                        rubric_formable_id: 0,
                        rubric_formable_type: 'Domain\\Profile\\VSO\\Models\\GradableMoment',
                        rubric_id: skillSkillTree.rubrics[0].id,
                        updated_at: new Date(),
                    };
                });

                gradableMoments.unshift({
                    id: 0,
                    created_at: new Date(),
                    updated_at: new Date(),
                    personal_goal_grades,
                    reviewer_id: 0,
                    rubric_forms: rubricForms,
                    season_id:
                        this.selectedSeason !== null
                            ? this.selectedSeason
                            : this.seasonsIn.length > 0
                              ? this.seasonsIn[this.seasonsIn.length - 1].id
                              : null,
                    text: 'Nieuwe doelen',
                    user_id: 0,
                    value: 0,
                });
            }

            return [...gradableMoments];
        },

        seasons() {
            let seasons = [];

            if (this.seasonsIn.length > 0) {
                seasons = this.seasonsIn
                    .filter((season) =>
                        this.gradableMoments.some((gradableMoment) => gradableMoment.season_id === season.id),
                    )
                    .map((season) => {
                        return {
                            value: season.id,
                            text: season.season,
                        };
                    });
            }

            if (!this.gradableMoments.length && this.personalGoals.length > 0) {
                const currentSeason = this.seasonsIn.filter((season) => season.isCurrentSeason === true).pop();
                seasons.push({
                    value: currentSeason.id,
                    text: currentSeason.season,
                });
            }

            seasons.unshift({
                value: null,
                text: this.$t('group.choose_school_year'),
                disabled: true,
            });

            return seasons;
        },

        selectedGradableMoment() {
            return this.gradableMomentsComputed.find(
                (gradableMoment) => gradableMoment.id === this.selectedGradableMomentId,
            );
        },
    },
    mounted() {
        this.selectedGradableMomentId = this.gradableMomentsComputed.length
            ? this.gradableMomentsComputed[this.gradableMomentsComputed.length - 1].id
            : null;
    },
    methods: {
        storePersonalGoal(personalGoals) {
            let data = {
                primary_personal_goal: personalGoals.primaryPersonalGoalTitle,
                secondary_personal_goal: personalGoals.secondaryPersonalGoalTitle,
                user_id: this.userCredentialsIn.id,
            };

            this.alert.saving();
            this.axios.post(this.routes.storeDualPersonalGoal(), data).then(
                (response) => {
                    this.personalGoals = response.data;
                    this.personalGoalsUpdater++;
                    this.ungradedCurrentPersonalGoals = true;
                    this.selectedGradableMomentId = 0;
                    this.selectedSeason =
                        this.seasonsIn.length > 0
                            ? this.seasonsIn.filter((season) => season.isCurrentSeason === true).pop().id
                            : null;
                    this.alert.success();
                },

                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        storeGrades(gradableMomentData) {
            let skill_grades = [];
            gradableMomentData.skillTree.skill_skill_trees.forEach((skillTree) => {
                skillTree.rubrics[0].rubric_sections[0].rubric_rows.forEach((row) => {
                    skill_grades.push({
                        column_id: row.answers[0].column_id,
                        row_id: row.answers[0].row_id,
                        skill_skill_tree_id: skillTree.id,
                    });
                });
            });

            let data = {
                user_id: this.userCredentialsIn.id,
                personal_goals: gradableMomentData.personalGoals.map((goal) => {
                    return {
                        personal_goal_id: goal.id,
                        grade: goal.grade,
                    };
                }),
                skill_grades: skill_grades,
            };

            this.alert.saving();
            this.axios.post(this.routes.vsoStylegradableMoment(), data).then(
                (response) => {
                    this.personalGoals = this.personalGoals.map((goal) => {
                        if (!goal.archived) {
                            const updatedGoal = response.data.personal_goals.find((newGoal) => newGoal.id === goal.id);

                            if (updatedGoal) {
                                return {
                                    ...goal,
                                    grades: updatedGoal.grades,
                                };
                            }
                        }
                        return goal;
                    });

                    this.personalGoals = this.personalGoals.filter((personalGoal) => {
                        personalGoal.archive !== 1;
                    });
                    this.personalGoals = this.personalGoals.concat(response.data.personal_goals);
                    const updatedGradableMoment = { ...response.data.gradable_moment };
                    updatedGradableMoment.value = response.data.gradable_moment.id;
                    updatedGradableMoment.text = new Date(response.data.gradable_moment.created_at)
                        .toLocaleDateString('nl-NL')
                        .replace(/\//g, '-');
                    this.gradableMoments.push(updatedGradableMoment);
                    this.selectedGradableMomentId = response.data.gradable_moment.id;
                    this.vsoSkillTree = { ...response.data.skillTree };

                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        storeStudentEvaluation(studentEvaluationData) {
            studentEvaluationData.user_id = this.userCredentialsIn.id;

            if (studentEvaluationData.id) {
                this.axios.post(this.routes.updateVsoStyleStudentEvaluation(), studentEvaluationData).then(
                    (response) => {
                        this.studentEvaluations.map((studentEvaluation) => {
                            if (studentEvaluation.id === response.data.id) {
                                return response.data;
                            }
                            return studentEvaluation;
                        });
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            } else {
                this.axios.post(this.routes.submitVsoStyleStudentEvaluation(), studentEvaluationData).then(
                    (response) => {
                        this.studentEvaluations.push(response.data);

                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }
        },

        showStatistics(data) {
            this.$refs.personalGoalStatistics.show(data);
        },

        correctRubricForm(skillSkillTreeId) {
            let skillSkillTree = this.vsoSkillTree.skill_skill_trees.find(
                (skillskillTree) => skillskillTree.id === skillSkillTreeId,
            );

            if (this.selectedGradableMoment) {
                return this.selectedGradableMoment.rubric_forms.find((rubricForm) => {
                    return skillSkillTree.rubrics.some((rubric) => rubric.id === rubricForm.rubric_id);
                });
            }
        },
    },
};
</script>

<style scoped lang="scss">
.combined-personal-goals {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
}

.form-group {
    &,
    select {
        width: 100%;
        margin-bottom: 0;
    }
}

::v-deep .btn-show-personal-goals-history {
    i {
        color: $primary;
    }
}

::v-deep .btn-evaluation-overview {
    i {
        color: $primary;
    }
}

.component-title {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    row-gap: map-get($grid-gutter-spacing, 'fifteen');
    margin-bottom: $grid-gutter-width;

    .season-select,
    .interview-time-select {
        width: 100%;
    }

    select {
        margin-bottom: 0;
    }
}

.personal-goals-wrapper,
.skills-wrapper {
    .title {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.personal-goals {
    margin-bottom: $grid-gutter-width;
    border-bottom: 1px solid $border-color;

    &.empty {
        justify-content: center;
        margin-top: map-get($grid-gutter-spacing, 'sixty');
    }
}

.skills {
    &.empty {
        justify-content: center;
        margin-top: map-get($grid-gutter-spacing, 'sixty');
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .form-group {
        flex: 0 0 auto;
        max-width: 350px;
        width: auto;
    }

    .header {
        flex-direction: row;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        flex-wrap: wrap;
        row-gap: map-get($grid-padding-spacing, 'fifteen');

        .season-select {
            width: 160px;
        }

        .interview-time-select {
            width: 200px;
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .content {
        position: relative;
        display: flex;
        justify-content: space-between;
        gap: map-get($grid-gutter-spacing, 'fifteen') map-get($grid-gutter-spacing, 'sixty');

        &:after {
            content: '';
            display: block;
            position: absolute;
            left: 50%;
            width: 2px;
            height: 100%;
            background-color: $border-color;
        }
    }

    .personal-goals {
        padding-bottom: 0;
        border-bottom: none;
    }

    .personal-goals-wrapper,
    .skills-wrapper {
        flex: 0 0 calc(50% - 30px);
    }
}

@media (min-width: 1600px) {
    .skills {
        flex-wrap: wrap;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: $grid-gutter-width;

        .skill-card {
            flex: 0 0 calc(50% - 15px);
        }
    }
}
</style>
